import React, { useState, useEffect } from 'react';
import { Download, X, Smartphone, ShieldCheck, HelpCircle } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export const PWAInstallBanner: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isSamsung, setIsSamsung] = useState(false);
  const [showGuideModal, setShowGuideModal] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already in standalone/PWA mode
    const standalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as unknown as { standalone?: boolean }).standalone === true;

    setIsStandalone(Boolean(standalone));

    // Check if user dismissed banner recently
    const dismissed = sessionStorage.getItem('blishka_pwa_dismissed');
    if (dismissed || standalone) {
      return;
    }

    // Detect OS & Browser
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIosDevice = /iphone|ipad|ipod/.test(userAgent);
    const isAndroidDevice = /android/.test(userAgent);
    const isSamsungBrowser = /samsungbrowser/.test(userAgent);

    setIsIOS(isIosDevice);
    setIsAndroid(isAndroidDevice);
    setIsSamsung(isSamsungBrowser);

    if (isIosDevice && !standalone) {
      // Show iOS instruction banner after slight delay on mobile
      const timer = setTimeout(() => setShowBanner(true), 3500);
      return () => clearTimeout(timer);
    }

    // Android/Chrome beforeinstallprompt listener
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setShowBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = async () => {
    if (isSamsung) {
      // In Samsung Internet, opening guide first shows the 100% zero-alert method
      setShowGuideModal(true);
      return;
    }

    if (!deferredPrompt) {
      setShowGuideModal(true);
      return;
    }

    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowBanner(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    sessionStorage.setItem('blishka_pwa_dismissed', 'true');
  };

  const isPortalRoute =
    typeof window !== 'undefined' &&
    (window.location.pathname.includes('/staff') ||
      window.location.pathname.includes('/partner') ||
      window.location.pathname.includes('/admin') ||
      window.location.pathname.includes('/super-admin') ||
      window.location.hash.includes('staff') ||
      window.location.hash.includes('partner') ||
      window.location.hash.includes('admin'));

  if (!showBanner || isStandalone || isPortalRoute) {
    return null;
  }

  return (
    <>
      <div className="pwa-install-banner" role="banner" aria-label="Install App Prompt">
        <div className="pwa-banner-content">
          <div className="pwa-app-icon">
            <img src="/brand-logo.png" alt="Blishka App" className="pwa-brand-img" />
          </div>

          <div className="pwa-text-block">
            <div className="pwa-title-row">
              <span className="pwa-title">Install Blishka App</span>
              <span className="pwa-verified-tag">
                <ShieldCheck size={11} /> 100% Safe
              </span>
            </div>
            <span className="pwa-subtitle">
              {isIOS
                ? 'Tap Share ⎋ → "Add to Home Screen"'
                : isSamsung
                ? 'Samsung 1-Tap Home Screen Setup'
                : 'Doorstep parlour app · Fast & Ad-free'}
            </span>
          </div>

          <div className="pwa-actions">
            {(isAndroid || isSamsung) && (
              <button
                type="button"
                className="pwa-info-btn"
                onClick={() => setShowGuideModal(true)}
                title={isSamsung ? 'Samsung Internet Guide' : 'Install Guide'}
                aria-label="Install Guide"
              >
                <HelpCircle size={15} />
              </button>
            )}

            <button
              type="button"
              className="pwa-install-btn"
              onClick={handleInstallClick}
              aria-label="Install Blishka Progressive Web App"
            >
              {isIOS ? <Smartphone size={13} /> : <Download size={13} />}
              <span>{isIOS ? 'How to Add' : isSamsung ? 'Add App' : 'Install'}</span>
            </button>

            <button
              type="button"
              className="pwa-close-btn"
              onClick={handleDismiss}
              aria-label="Dismiss install banner"
            >
              <X size={15} />
            </button>
          </div>
        </div>
      </div>

      {showGuideModal && (
        <div className="pwa-guide-backdrop" onClick={() => setShowGuideModal(false)}>
          <div className="pwa-guide-card" onClick={(e) => e.stopPropagation()}>
            <div className="pwa-guide-header">
              <div className="pwa-guide-icon-badge">
                <img src="/brand-logo.png" alt="Blishka Logo" className="pwa-modal-brand-img" />
              </div>
              <div className="pwa-guide-title-box">
                <h4 className="pwa-guide-title">
                  {isIOS
                    ? 'Install on iPhone / iPad'
                    : isSamsung
                    ? 'Avoid Samsung Alert (Easy Fix)'
                    : 'Android Safe Installation Guide'}
                </h4>
                <p className="pwa-guide-sub">Official Blishka Progressive Web App</p>
              </div>
              <button
                type="button"
                className="pwa-guide-close"
                onClick={() => setShowGuideModal(false)}
                aria-label="Close guide"
              >
                <X size={18} />
              </button>
            </div>

            <div className="pwa-guide-body">
              {isIOS ? (
                <ol className="pwa-steps-list">
                  <li>Tap the <strong>Share button (⎋)</strong> at the bottom of Safari.</li>
                  <li>Scroll down and select <strong>"Add to Home Screen"</strong>.</li>
                  <li>Tap <strong>Add</strong> in the top right corner.</li>
                </ol>
              ) : isSamsung ? (
                <div className="pwa-android-help-content">
                  <div className="pwa-highlight-box">
                    <p className="pwa-highlight-title">⭐ Best Way: 100% Zero-Alert Method</p>
                    <p className="pwa-highlight-text">
                      In Samsung Internet, you can add Blishka directly to your Home Screen without Samsung&apos;s WebAPK alert triggering at all:
                    </p>
                    <ol className="pwa-steps-list" style={{ marginTop: '0.4rem' }}>
                      <li>
                        Tap Samsung&apos;s <strong>Menu icon (☰)</strong> at the bottom-right corner.
                      </li>
                      <li>
                        Tap <strong>&quot;+ Add page to&quot;</strong>.
                      </li>
                      <li>
                        Select <strong>&quot;Home screen&quot;</strong> $\rightarrow$ Tap <strong>&quot;Add&quot;</strong>.
                      </li>
                    </ol>
                    <p className="pwa-highlight-tip">
                      ✨ Done! It opens as a full fullscreen app with zero warnings!
                    </p>
                  </div>

                  <div className="pwa-notice-box" style={{ marginTop: '0.75rem' }}>
                    <p className="pwa-notice-title">💡 If you already clicked Install and see the Alert:</p>
                    <p className="pwa-notice-text">
                      Samsung uses an older background packager that Android 14 flags. Simply tap <strong>&quot;More details&quot;</strong> $\rightarrow$ <strong>&quot;Install anyway&quot;</strong>. Blishka is 100% verified, secure, and virus-free.
                    </p>
                  </div>

                  <p className="pwa-alt-note">
                    👉 <em>Prefer Chrome?</em> Opening <strong>blishka.pages.dev</strong> in <strong>Google Chrome</strong> installs directly in 1-tap with zero alerts.
                  </p>
                </div>
              ) : (
                <div className="pwa-android-help-content">
                  <div className="pwa-notice-box">
                    <p className="pwa-notice-title">💡 Why Android warns about &quot;Older Version&quot;:</p>
                    <p className="pwa-notice-text">
                      Android 14+ Play Protect shows this automated alert when installing web apps from certain OEM browsers (like Samsung Internet or Mi Browser) because their internal packager uses an older wrapper.
                    </p>
                  </div>

                  <h5 className="pwa-steps-heading">How to proceed safely:</h5>
                  <ol className="pwa-steps-list">
                    <li>
                      When the prompt appears, tap <strong>&quot;More details&quot;</strong>.
                    </li>
                    <li>
                      Select <strong>&quot;Install anyway&quot;</strong>. Blishka is 100% verified, runs in a secure browser sandbox, and has zero invasive permissions.
                    </li>
                    <li>
                      <em>Alternative:</em> Open <strong>blishka.pages.dev</strong> in <strong>Google Chrome</strong> for direct 1-tap installation without any warning.
                    </li>
                  </ol>
                </div>
              )}
            </div>

            <div className="pwa-guide-footer">
              {isSamsung && deferredPrompt && (
                <button
                  type="button"
                  className="pwa-guide-btn-secondary"
                  onClick={() => {
                    setShowGuideModal(false);
                    deferredPrompt.prompt();
                  }}
                >
                  Install via WebAPK
                </button>
              )}
              <button
                type="button"
                className="pwa-guide-btn-primary"
                onClick={() => {
                  setShowGuideModal(false);
                  if (!isSamsung && !isIOS && deferredPrompt) {
                    deferredPrompt.prompt();
                  }
                }}
              >
                Got It, Thanks!
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
